import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  useMutation,
  useQueryClient,
  type QueryKey
} from "@tanstack/react-query";
import { HugeiconsIcon } from "@hugeicons/react";
import { AlertCircleIcon } from "@hugeicons-pro/core-stroke-rounded";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import { Banner } from "shared/components/Banner";
import { Row } from "views/applications-id-report/components/Row";
import { SectionCard } from "shared/app/SectionCard";
import { ListingAppearanceModal } from "shared/app/ListingAppearanceModal";
import { validateCustomButtonLink } from "shared/app/ListingAppearanceModal/validateCustomButtonLink";
import { ApplyCustomButtonLinkModal } from "shared/app/ApplyCustomButtonLink/ApplyCustomButtonLinkModal";
import { CustomButtonLinkScope } from "types/landlord/address";
import { getErrorMessage } from "utils/error";

interface ListingAppearanceSectionProps {
  customButtonLink?: string | null;
  inheritedLink?: string | null;
  inheritedFromLabel?: string;
  showWarning: boolean;
  warningText: string;
  inputId: string;
  saveCustomButtonLink: (value: string) => Promise<unknown>;
  successToast: string;
  errorToastPrefix: string;
  invalidateQueryKey: QueryKey;
  bulkApply?: {
    scope: CustomButtonLinkScope;
    sourceId: number;
    sourceName: string;
    actionLabel: string;
  };
  pmsSync?: {
    syncWithPms: boolean;
    pmsValue?: string | null;
    saveSyncWithPms: (syncWithPms: boolean) => Promise<unknown>;
  };
}

// Shared by properties-id and properties-id-units-id: the two entities differ
// only in which API call saves the link, the toast copy, the warning text and
// which query to invalidate — everything else (card, modal, validation) is
// identical, so it's parameterized here instead of duplicated per entity.
export const ListingAppearanceSection = ({
  customButtonLink,
  inheritedLink,
  inheritedFromLabel,
  showWarning,
  warningText,
  inputId,
  saveCustomButtonLink,
  successToast,
  errorToastPrefix,
  invalidateQueryKey,
  bulkApply,
  pmsSync
}: ListingAppearanceSectionProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isBulkApplyOpen, setIsBulkApplyOpen] = useState(false);
  const [pendingLink, setPendingLink] = useState<string | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    setPendingLink(null);
  }, [customButtonLink]);

  const ownLink = pendingLink ?? customButtonLink;
  const isInherited = !ownLink && Boolean(inheritedLink);
  const displayValue = ownLink || inheritedLink;
  const isValidLink =
    Boolean(displayValue) && !validateCustomButtonLink(displayValue ?? "");

  const linkNode = isValidLink ? (
    <a
      href={displayValue ?? undefined}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary-500 underline hover:text-primary-600"
    >
      {displayValue}
    </a>
  ) : (
    displayValue
  );

  const { mutate: save, isPending } = useMutation({
    mutationFn: ({
      value,
      syncWithPms
    }: {
      value: string;
      syncWithPms?: boolean;
    }) =>
      syncWithPms === true && pmsSync
        ? pmsSync.saveSyncWithPms(true)
        : saveCustomButtonLink(value),
    onSuccess: (_data, { value, syncWithPms }) => {
      setPendingLink(syncWithPms === true ? null : value);
      toast.success(successToast);
      queryClient.invalidateQueries({ queryKey: invalidateQueryKey });
      setIsEditing(false);
    },
    onError: (err) => {
      toast.error(`${errorToastPrefix}: ${getErrorMessage(err)}`);
    }
  });

  return (
    <>
      <SectionCard
        title="Listing appearance"
        headerExtra={
          <Button
            size={ButtonSize.SM}
            variant={ButtonVariant.BASE}
            onClick={() => setIsEditing(true)}
          >
            Edit
          </Button>
        }
      >
        <div className="space-y-4">
          <Show if={showWarning}>
            <Banner
              variant="warning"
              HugeIcon={
                <HugeiconsIcon
                  icon={AlertCircleIcon}
                  size={20}
                  className="text-warning-600"
                />
              }
              body={warningText}
            />
          </Show>
          <Row
            title="Custom button link"
            valueClassName="flex-1 w-auto min-w-0 break-all"
            value={
              isInherited ? (
                <>
                  {linkNode}
                  <br />
                  <span className="text-content-300">
                    (from {inheritedFromLabel})
                  </span>
                </>
              ) : (
                linkNode
              )
            }
          />
          <Show if={Boolean(bulkApply && ownLink)}>
            <Button
              size={ButtonSize.SM}
              variant={ButtonVariant.TEXT}
              onClick={() => setIsBulkApplyOpen(true)}
            >
              {bulkApply?.actionLabel}
            </Button>
          </Show>
        </div>
      </SectionCard>
      <ListingAppearanceModal
        opened={isEditing}
        onClose={() => setIsEditing(false)}
        inputId={inputId}
        initialValue={ownLink}
        isSaving={isPending}
        onSave={(value, syncWithPms) => save({ value, syncWithPms })}
        pmsSync={
          pmsSync
            ? { syncWithPms: pmsSync.syncWithPms, pmsValue: pmsSync.pmsValue }
            : undefined
        }
      />
      {bulkApply && ownLink ? (
        <ApplyCustomButtonLinkModal
          opened={isBulkApplyOpen}
          onClose={() => setIsBulkApplyOpen(false)}
          scope={bulkApply.scope}
          sourceId={bulkApply.sourceId}
          sourceName={bulkApply.sourceName}
          link={ownLink}
        />
      ) : null}
    </>
  );
};
